import React, { useState } from 'react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import DarkLayout from './DarkLayout';
import TabNav from './TabNav';
import CategoriesTab from './CategoriesTab';
import GroupsTab from './GroupsTab';
import UnitsTab from './UnitsTab';
import DxfLayerRulesTab from './DxfLayerRulesTab';
import { useHeaderCreateRegistration } from './headerCreateAction';
import { HeaderPrimaryAddButton } from './HeaderPrimaryAddButton';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, permissions } from '../constants/permissions';

const MasterDataManagement: React.FC = () => {
  const { user } = useAuth();
  const canViewMasterData = hasPermission(user, permissions.masterData.view);
  const canViewDxfRules = hasPermission(user, permissions.masterData.viewDxfRules);
  useDocumentTitle('Master Data');
  const [activeTab, setActiveTab] = useState('categories');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [headerCreate, registerCreateAction] = useHeaderCreateRegistration();

  const headerAddLabels: Record<string, string> = {
    categories: 'Add Category',
    groups: 'Add Group',
    units: 'Add Unit',
    'dxf-rules': 'Add Rule',
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleSuccess = (successMessage: string) => {
    setSuccess(successMessage);
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const tabs = [
    canViewMasterData ? { id: 'categories', label: 'Categories', icon: 'category' } : null,
    canViewMasterData ? { id: 'groups', label: 'Groups', icon: 'folder' } : null,
    canViewMasterData ? { id: 'units', label: 'Units', icon: 'straighten' } : null,
    canViewDxfRules ? { id: 'dxf-rules', label: 'DXF layer rules', icon: 'layers' } : null,
  ].filter(Boolean) as { id: string; label: string; icon: string }[];

  React.useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTab) && tabs.length > 0) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  return (
    <DarkLayout 
      title="Master Data Management" 
      activeTab={activeTab}
      onTabChange={handleTabChange}
      headerRight={
        headerCreate && headerAddLabels[activeTab] ? (
          <HeaderPrimaryAddButton
            label={headerAddLabels[activeTab]}
            onClick={() => headerCreate.run()}
          />
        ) : null
      }
    >
      {/* Custom Tab Navigation */}
      <TabNav
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {/* Tab Content */}
      <div className="min-h-[calc(100vh-200px)]">
        {activeTab === 'categories' && canViewMasterData && (
          <CategoriesTab
            onError={handleError}
            onSuccess={handleSuccess}
            onRegisterCreateAction={registerCreateAction}
          />
        )}
        {activeTab === 'groups' && canViewMasterData && (
          <GroupsTab
            onError={handleError}
            onSuccess={handleSuccess}
            onRegisterCreateAction={registerCreateAction}
          />
        )}
        {activeTab === 'units' && canViewMasterData && (
          <UnitsTab
            onError={handleError}
            onSuccess={handleSuccess}
            onRegisterCreateAction={registerCreateAction}
          />
        )}
        {activeTab === 'dxf-rules' && canViewDxfRules && (
          <DxfLayerRulesTab
            onError={handleError}
            onSuccess={handleSuccess}
            onRegisterCreateAction={registerCreateAction}
          />
        )}
      </div>

      {/* Error Notification */}
      {error && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 text-sm max-w-md">
          <div className="flex items-center justify-between">
            <span>{error}</span>
            <button 
              onClick={() => setError('')}
              className="ml-4 text-red-400 hover:text-red-300"
            >
              <span className="material-symbols-outlined text-base">close</span>
            </button>
          </div>
        </div>
      )}

      {/* Success Notification */}
      {success && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-green-500/10 border border-green-500/20 rounded-lg p-4 text-green-400 text-sm max-w-md">
          <div className="flex items-center justify-between">
            <span>{success}</span>
            <button 
              onClick={() => setSuccess('')}
              className="ml-4 text-green-400 hover:text-green-300"
            >
              <span className="material-symbols-outlined text-base">close</span>
            </button>
          </div>
        </div>
      )}
    </DarkLayout>
  );
};

export default MasterDataManagement;

